import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2, Building2, Plus, X, Users, ChevronDown, ChevronUp } from 'lucide-react';

// Entity Types
const ENTITY_TYPES = [
  { value: 'Company', label: 'Australian Company (Pty Ltd / Public / CLG)' },
  { value: 'Trust', label: 'Trust (Discretionary / Unit / Hybrid / Testamentary / Charitable)' },
  { value: 'SMSF', label: 'SMSF (Self-Managed Super Fund)' },
  { value: 'Foundation', label: 'Foundation (AU Structures)' }
];

// Subtypes
const COMPANY_SUBTYPES = ['Pty Ltd', 'Public', 'CLG', 'Other'];
const TRUST_SUBTYPES = ['Discretionary', 'Unit', 'Hybrid', 'Testamentary', 'Charitable', 'Other'];
const FOUNDATION_STRUCTURES = ['CLG', 'Charitable Trust', 'Incorporated Association', 'PAF', 'PuAF', 'Other'];
const SHARE_STRUCTURES = ['Ordinary', 'Preference', 'Other'];
const PAID_STATUSES = ['Fully paid', 'Partly paid', 'Unpaid'];
const ATO_STATUSES = ['Complying', 'Non-complying', 'Unknown'];
const PAF_TYPES = ['None', 'PAF', 'PuAF'];

// Initial form data structure
const getInitialFormData = () => ({
  // Common fields
  name: '',
  type: '',
  abn: '',
  tfn: '',
  gstRegistered: false,
  
  // Company specific
  companySubtype: '',
  acn: '',
  asicAgentName: '',
  asicAgentNumber: '',
  annualReviewDate: '',
  previousName: '',
  ultimateHoldingCompanyName: '',
  ultimateHoldingCompanyAcnAbn: '',
  constitutionAdopted: false,
  constitutionAdoptionDate: '',
  directors: [],
  companySecretary: '',
  directorIdsRecorded: false,
  shareholders: [],
  shareStructure: '',
  issuedShares: [],
  paidStatus: '',
  
  // Trust specific
  trustSubtype: '',
  trusteeType: '',
  trustees: [],
  appointors: [],
  guardians: [],
  settlor: '',
  beneficiariesType: '',
  beneficiariesClasses: '',
  trustDeedDate: '',
  deedVersionReference: '',
  vestingDate: '',
  
  // SMSF specific
  atoStatus: '',
  esa: '',
  corporateTrusteeAcn: '',
  members: [],
  memberBalances: [],
  smsfDeedDate: '',
  investmentStrategyLastReviewed: '',
  auditor: '',
  administrator: '',
  bdbnInPlace: false,
  bdbnDateSigned: '',
  bdbnExpiryDate: '',
  pensionInPlace: false,
  pensionCommencementDate: '',
  
  // Foundation specific
  foundationStructure: '',
  acncRegistered: false,
  acncCharityId: '',
  charitySubtype: '',
  dgrEndorsed: false,
  dgrItemCategory: '',
  pafPuafType: '',
  responsiblePersons: [],
  minDistributionRequired: false,
  distributionPercentage: '',
  distributionYear: ''
});

// Repeating group item templates
const createDirector = () => ({ id: Date.now(), name: '', individualId: '', role: '', startDate: '', endDate: '' });
const createShareholder = () => ({ id: Date.now(), name: '', individualId: '', ownershipPercentage: '' });
const createTrustee = () => ({ id: Date.now(), name: '', individualId: '', acnAbn: '', startDate: '', endDate: '' });
const createAppointor = () => ({ id: Date.now(), name: '', individualId: '', startDate: '', endDate: '' });
const createGuardian = () => ({ id: Date.now(), name: '', individualId: '', startDate: '', endDate: '' });
const createMember = () => ({ id: Date.now(), name: '', individualId: '', memberNumber: '' });
const createMemberBalance = () => ({ id: Date.now(), memberName: '', balanceAmount: '', asAtDate: '' });
const createIssuedShare = () => ({ id: Date.now(), shareClass: '', numberIssued: '' });
const createResponsiblePerson = () => ({ id: Date.now(), name: '', individualId: '', role: '', startDate: '', endDate: '' });

export default function AddEntityModal({ isOpen, onClose, onSave, clientId, initialData, isEdit, individuals = [] }) {
  const [formData, setFormData] = useState(getInitialFormData());
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    identity: true,
    governance: false,
    ownership: false,
    parties: false,
    deed: false,
    compliance: false,
    distributions: false
  });

  useEffect(() => {
    if (isEdit && initialData) {
      setFormData({ ...getInitialFormData(), ...initialData });
    } else if (!isEdit) {
      setFormData(getInitialFormData());
    }
  }, [isEdit, initialData, isOpen]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Entity name is required';
    if (!formData.type) newErrors.type = 'Entity type is required';
    
    // Type-specific validation
    if (formData.type === 'Company' && !formData.companySubtype) {
      newErrors.companySubtype = 'Company subtype is required';
    }
    if (formData.type === 'Trust' && !formData.trustSubtype) {
      newErrors.trustSubtype = 'Trust subtype is required';
    }
    if (formData.type === 'Foundation' && !formData.foundationStructure) {
      newErrors.foundationStructure = 'Foundation structure is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const entityData = {
        ...formData,
        clientId: clientId
      };
      
      await onSave(entityData);
      setFormData(getInitialFormData());
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Failed to save entity:', error);
      setErrors({ submit: 'Failed to save entity. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData(getInitialFormData());
    setErrors({});
    onClose();
  };

  // Helper to update array fields
  const updateArrayItem = (field, index, key, value) => {
    const newArray = [...formData[field]];
    newArray[index] = { ...newArray[index], [key]: value };
    setFormData({ ...formData, [field]: newArray });
  };

  const addArrayItem = (field, createFn) => {
    setFormData({ ...formData, [field]: [...formData[field], createFn()] });
  };

  const removeArrayItem = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  // Individual selector dropdown component
  const IndividualSelect = ({ value, onChange, placeholder = "Select individual..." }) => (
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger className="caffeine-input">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="">-- Manual Entry --</SelectItem>
        {individuals.map(ind => (
          <SelectItem key={ind.id} value={ind.id}>
            {ind.firstName} {ind.lastName} {ind.familyRole ? `(${ind.familyRole})` : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  // Section header component
  const SectionHeader = ({ title, section, icon: Icon }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between py-3 px-4 bg-[#FAF8F5] rounded-lg hover:bg-[#F5F3F1] transition-colors"
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#4A3B32]" />}
        <span className="font-medium text-[#2C2420]">{title}</span>
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="w-4 h-4 text-[#8C8078]" />
      ) : (
        <ChevronDown className="w-4 h-4 text-[#8C8078]" />
      )}
    </button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] bg-white max-h-[90vh] overflow-y-auto" data-testid="add-entity-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl text-[#2C2420]">
            <Building2 className="w-5 h-5 text-[#4A3B32]" />
            {isEdit ? 'Edit Entity' : 'Add New Entity'}
          </DialogTitle>
          <DialogDescription className="text-[#5D524D]">
            {isEdit ? 'Update the details for this entity.' : 'Add a new entity to this family group.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Entity Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-[#2C2420]">
              Entity Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Smith Holdings Pty Ltd"
              className={`caffeine-input ${errors.name ? 'border-red-500' : ''}`}
              data-testid="entity-name-input"
            />
            {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
          </div>

          {/* Entity Type */}
          <div className="space-y-2">
            <Label className="text-[#2C2420]">
              Entity Type <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.type}
              onValueChange={(value) => setFormData({ ...formData, type: value })}
            >
              <SelectTrigger className={`caffeine-input ${errors.type ? 'border-red-500' : ''}`} data-testid="entity-type-select">
                <SelectValue placeholder="Select entity type..." />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.type && <p className="text-sm text-red-500">{errors.type}</p>}
          </div>

          {/* ============ COMPANY FIELDS ============ */}
          {formData.type === 'Company' && (
            <>
              {/* Identity Section */}
              <SectionHeader title="ASIC / Identity" section="identity" icon={Building2} />
              {expandedSections.identity && (
                <div className="space-y-4 pl-4 border-l-2 border-[#E6E0D9]">
                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Company Subtype <span className="text-red-500">*</span></Label>
                    <Select
                      value={formData.companySubtype}
                      onValueChange={(value) => setFormData({ ...formData, companySubtype: value })}
                    >
                      <SelectTrigger className={`caffeine-input ${errors.companySubtype ? 'border-red-500' : ''}`} data-testid="company-subtype-select">
                        <SelectValue placeholder="Select subtype..." />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPANY_SUBTYPES.map(sub => (
                          <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.companySubtype && <p className="text-sm text-red-500">{errors.companySubtype}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">ACN</Label>
                      <Input
                        value={formData.acn}
                        onChange={(e) => setFormData({ ...formData, acn: e.target.value })}
                        placeholder="123 456 789"
                        className="caffeine-input"
                        data-testid="entity-acn-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">ABN</Label>
                      <Input
                        value={formData.abn}
                        onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                        placeholder="12 345 678 901"
                        className="caffeine-input"
                        data-testid="entity-abn-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">TFN</Label>
                    <Input
                      value={formData.tfn}
                      onChange={(e) => setFormData({ ...formData, tfn: e.target.value })}
                      placeholder="123 456 789"
                      className="caffeine-input"
                      data-testid="entity-tfn-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">ASIC Agent Name</Label>
                      <Input
                        value={formData.asicAgentName}
                        onChange={(e) => setFormData({ ...formData, asicAgentName: e.target.value })}
                        className="caffeine-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">ASIC Agent Number</Label>
                      <Input
                        value={formData.asicAgentNumber}
                        onChange={(e) => setFormData({ ...formData, asicAgentNumber: e.target.value })}
                        className="caffeine-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Annual Review Date</Label>
                    <Input
                      type="date"
                      value={formData.annualReviewDate}
                      onChange={(e) => setFormData({ ...formData, annualReviewDate: e.target.value })}
                      className="caffeine-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Previous Name</Label>
                    <Input
                      value={formData.previousName}
                      onChange={(e) => setFormData({ ...formData, previousName: e.target.value })}
                      className="caffeine-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">Ultimate Holding Company Name</Label>
                      <Input
                        value={formData.ultimateHoldingCompanyName}
                        onChange={(e) => setFormData({ ...formData, ultimateHoldingCompanyName: e.target.value })}
                        className="caffeine-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">Ultimate Holding Company ACN/ABN</Label>
                      <Input
                        value={formData.ultimateHoldingCompanyAcnAbn}
                        onChange={(e) => setFormData({ ...formData, ultimateHoldingCompanyAcnAbn: e.target.value })}
                        className="caffeine-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Governance Section */}
              <SectionHeader title="Governance" section="governance" icon={Users} />
              {expandedSections.governance && (
                <div className="space-y-4 pl-4 border-l-2 border-[#E6E0D9]">
                  <div className="flex items-center justify-between">
                    <Label className="text-[#2C2420]">Constitution Adopted</Label>
                    <Switch
                      checked={formData.constitutionAdopted}
                      onCheckedChange={(checked) => setFormData({ ...formData, constitutionAdopted: checked })}
                    />
                  </div>

                  {formData.constitutionAdopted && (
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">Constitution Adoption Date</Label>
                      <Input
                        type="date"
                        value={formData.constitutionAdoptionDate}
                        onChange={(e) => setFormData({ ...formData, constitutionAdoptionDate: e.target.value })}
                        className="caffeine-input"
                      />
                    </div>
                  )}

                  {/* Directors */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#2C2420]">Directors</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addArrayItem('directors', createDirector)}
                        className="caffeine-btn-ghost"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Director
                      </Button>
                    </div>
                    {formData.directors.map((director, index) => (
                      <div key={director.id} className="p-3 bg-[#FAF8F5] rounded-lg space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-[#8C8078]">Director {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeArrayItem('directors', index)}
                            className="text-red-500 hover:text-red-700 p-1 h-auto"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-[#5D524D]">Select Individual</Label>
                          <IndividualSelect
                            value={director.individualId}
                            onChange={(value) => {
                              const ind = individuals.find(i => i.id === value);
                              updateArrayItem('directors', index, 'individualId', value);
                              if (ind) {
                                updateArrayItem('directors', index, 'name', `${ind.firstName} ${ind.lastName}`);
                              }
                            }}
                          />
                        </div>
                        <Input
                          value={director.name}
                          onChange={(e) => updateArrayItem('directors', index, 'name', e.target.value)}
                          placeholder="Full Name"
                          className="caffeine-input"
                        />
                        <Input
                          value={director.role}
                          onChange={(e) => updateArrayItem('directors', index, 'role', e.target.value)}
                          placeholder="Role (e.g., Managing Director)"
                          className="caffeine-input"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={director.startDate}
                            onChange={(e) => updateArrayItem('directors', index, 'startDate', e.target.value)}
                            placeholder="Start Date"
                            className="caffeine-input text-sm"
                          />
                          <Input
                            type="date"
                            value={director.endDate}
                            onChange={(e) => updateArrayItem('directors', index, 'endDate', e.target.value)}
                            placeholder="End Date"
                            className="caffeine-input text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Company Secretary</Label>
                    <Input
                      value={formData.companySecretary}
                      onChange={(e) => setFormData({ ...formData, companySecretary: e.target.value })}
                      placeholder="Full Name"
                      className="caffeine-input"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="text-[#2C2420]">Director IDs Recorded</Label>
                    <Switch
                      checked={formData.directorIdsRecorded}
                      onCheckedChange={(checked) => setFormData({ ...formData, directorIdsRecorded: checked })}
                    />
                  </div>
                </div>
              )}

              {/* Ownership Section */}
              <SectionHeader title="Ownership / Capital" section="ownership" icon={Building2} />
              {expandedSections.ownership && (
                <div className="space-y-4 pl-4 border-l-2 border-[#E6E0D9]">
                  {/* Shareholders */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#2C2420]">Shareholders</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addArrayItem('shareholders', createShareholder)}
                        className="caffeine-btn-ghost"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Shareholder
                      </Button>
                    </div>
                    {formData.shareholders.map((sh, index) => (
                      <div key={sh.id} className="p-3 bg-[#FAF8F5] rounded-lg space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-[#8C8078]">Shareholder {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeArrayItem('shareholders', index)}
                            className="text-red-500 hover:text-red-700 p-1 h-auto"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-[#5D524D]">Select Individual</Label>
                          <IndividualSelect
                            value={sh.individualId}
                            onChange={(value) => {
                              const ind = individuals.find(i => i.id === value);
                              updateArrayItem('shareholders', index, 'individualId', value);
                              if (ind) {
                                updateArrayItem('shareholders', index, 'name', `${ind.firstName} ${ind.lastName}`);
                              }
                            }}
                          />
                        </div>
                        <Input
                          value={sh.name}
                          onChange={(e) => updateArrayItem('shareholders', index, 'name', e.target.value)}
                          placeholder="Name"
                          className="caffeine-input"
                        />
                        <Input
                          type="number"
                          value={sh.ownershipPercentage}
                          onChange={(e) => updateArrayItem('shareholders', index, 'ownershipPercentage', e.target.value)}
                          placeholder="% Ownership"
                          className="caffeine-input"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Share Structure</Label>
                    <Select
                      value={formData.shareStructure}
                      onValueChange={(value) => setFormData({ ...formData, shareStructure: value })}
                    >
                      <SelectTrigger className="caffeine-input">
                        <SelectValue placeholder="Select structure..." />
                      </SelectTrigger>
                      <SelectContent>
                        {SHARE_STRUCTURES.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Issued Shares */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#2C2420]">Issued Shares</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addArrayItem('issuedShares', createIssuedShare)}
                        className="caffeine-btn-ghost"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Class
                      </Button>
                    </div>
                    {formData.issuedShares.map((share, index) => (
                      <div key={share.id} className="p-3 bg-[#FAF8F5] rounded-lg space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-[#8C8078]">Share Class {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeArrayItem('issuedShares', index)}
                            className="text-red-500 hover:text-red-700 p-1 h-auto"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={share.shareClass}
                            onChange={(e) => updateArrayItem('issuedShares', index, 'shareClass', e.target.value)}
                            placeholder="Class (e.g., Ordinary A)"
                            className="caffeine-input"
                          />
                          <Input
                            type="number"
                            value={share.numberIssued}
                            onChange={(e) => updateArrayItem('issuedShares', index, 'numberIssued', e.target.value)}
                            placeholder="Number Issued"
                            className="caffeine-input"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Paid Status</Label>
                    <Select
                      value={formData.paidStatus}
                      onValueChange={(value) => setFormData({ ...formData, paidStatus: value })}
                    >
                      <SelectTrigger className="caffeine-input">
                        <SelectValue placeholder="Select status..." />
                      </SelectTrigger>
                      <SelectContent>
                        {PAID_STATUSES.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ============ TRUST FIELDS ============ */}
          {formData.type === 'Trust' && (
            <>
              {/* Core Section */}
              <SectionHeader title="Core" section="identity" icon={Building2} />
              {expandedSections.identity && (
                <div className="space-y-4 pl-4 border-l-2 border-[#E6E0D9]">
                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Trust Subtype <span className="text-red-500">*</span></Label>
                    <Select
                      value={formData.trustSubtype}
                      onValueChange={(value) => setFormData({ ...formData, trustSubtype: value })}
                    >
                      <SelectTrigger className={`caffeine-input ${errors.trustSubtype ? 'border-red-500' : ''}`} data-testid="trust-subtype-select">
                        <SelectValue placeholder="Select subtype..." />
                      </SelectTrigger>
                      <SelectContent>
                        {TRUST_SUBTYPES.map(sub => (
                          <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.trustSubtype && <p className="text-sm text-red-500">{errors.trustSubtype}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">ABN</Label>
                      <Input
                        value={formData.abn}
                        onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                        placeholder="12 345 678 901"
                        className="caffeine-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">TFN</Label>
                      <Input
                        value={formData.tfn}
                        onChange={(e) => setFormData({ ...formData, tfn: e.target.value })}
                        placeholder="123 456 789"
                        className="caffeine-input"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="text-[#2C2420]">GST Registered</Label>
                    <Switch
                      checked={formData.gstRegistered}
                      onCheckedChange={(checked) => setFormData({ ...formData, gstRegistered: checked })}
                    />
                  </div>
                </div>
              )}

              {/* Parties Section */}
              <SectionHeader title="Parties" section="parties" icon={Users} />
              {expandedSections.parties && (
                <div className="space-y-4 pl-4 border-l-2 border-[#E6E0D9]">
                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Trustee Type</Label>
                    <Select
                      value={formData.trusteeType}
                      onValueChange={(value) => setFormData({ ...formData, trusteeType: value })}
                    >
                      <SelectTrigger className="caffeine-input" data-testid="trustee-type-select">
                        <SelectValue placeholder="Select trustee type..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Individual">Individual</SelectItem>
                        <SelectItem value="Corporate">Corporate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Trustees */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#2C2420]">Trustee(s)</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addArrayItem('trustees', createTrustee)}
                        className="caffeine-btn-ghost"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Trustee
                      </Button>
                    </div>
                    {formData.trustees.map((trustee, index) => (
                      <div key={trustee.id} className="p-3 bg-[#FAF8F5] rounded-lg space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-[#8C8078]">Trustee {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeArrayItem('trustees', index)}
                            className="text-red-500 hover:text-red-700 p-1 h-auto"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        {formData.trusteeType === 'Individual' && (
                          <div className="space-y-2">
                            <Label className="text-xs text-[#5D524D]">Select Individual</Label>
                            <IndividualSelect
                              value={trustee.individualId}
                              onChange={(value) => {
                                const ind = individuals.find(i => i.id === value);
                                updateArrayItem('trustees', index, 'individualId', value);
                                if (ind) {
                                  updateArrayItem('trustees', index, 'name', `${ind.firstName} ${ind.lastName}`);
                                }
                              }}
                            />
                          </div>
                        )}
                        <Input
                          value={trustee.name}
                          onChange={(e) => updateArrayItem('trustees', index, 'name', e.target.value)}
                          placeholder={formData.trusteeType === 'Corporate' ? 'Company Name' : 'Full Name'}
                          className="caffeine-input"
                        />
                        {formData.trusteeType === 'Corporate' && (
                          <Input
                            value={trustee.acnAbn}
                            onChange={(e) => updateArrayItem('trustees', index, 'acnAbn', e.target.value)}
                            placeholder="ACN / ABN"
                            className="caffeine-input"
                          />
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={trustee.startDate}
                            onChange={(e) => updateArrayItem('trustees', index, 'startDate', e.target.value)}
                            className="caffeine-input text-sm"
                          />
                          <Input
                            type="date"
                            value={trustee.endDate}
                            onChange={(e) => updateArrayItem('trustees', index, 'endDate', e.target.value)}
                            className="caffeine-input text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Appointors */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#2C2420]">Appointor / Principal</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addArrayItem('appointors', createAppointor)}
                        className="caffeine-btn-ghost"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add
                      </Button>
                    </div>
                    {formData.appointors.map((app, index) => (
                      <div key={app.id} className="p-3 bg-[#FAF8F5] rounded-lg space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-[#8C8078]">Appointor {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeArrayItem('appointors', index)}
                            className="text-red-500 hover:text-red-700 p-1 h-auto"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <IndividualSelect
                          value={app.individualId}
                          onChange={(value) => {
                            const ind = individuals.find(i => i.id === value);
                            updateArrayItem('appointors', index, 'individualId', value);
                            if (ind) {
                              updateArrayItem('appointors', index, 'name', `${ind.firstName} ${ind.lastName}`);
                            }
                          }}
                        />
                        <Input
                          value={app.name}
                          onChange={(e) => updateArrayItem('appointors', index, 'name', e.target.value)}
                          placeholder="Name"
                          className="caffeine-input"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={app.startDate}
                            onChange={(e) => updateArrayItem('appointors', index, 'startDate', e.target.value)}
                            className="caffeine-input text-sm"
                          />
                          <Input
                            type="date"
                            value={app.endDate}
                            onChange={(e) => updateArrayItem('appointors', index, 'endDate', e.target.value)}
                            className="caffeine-input text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Guardians */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#2C2420]">Guardian</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addArrayItem('guardians', createGuardian)}
                        className="caffeine-btn-ghost"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add
                      </Button>
                    </div>
                    {formData.guardians.map((g, index) => (
                      <div key={g.id} className="p-3 bg-[#FAF8F5] rounded-lg space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-[#8C8078]">Guardian {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeArrayItem('guardians', index)}
                            className="text-red-500 hover:text-red-700 p-1 h-auto"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <IndividualSelect
                          value={g.individualId}
                          onChange={(value) => {
                            const ind = individuals.find(i => i.id === value);
                            updateArrayItem('guardians', index, 'individualId', value);
                            if (ind) {
                              updateArrayItem('guardians', index, 'name', `${ind.firstName} ${ind.lastName}`);
                            }
                          }}
                        />
                        <Input
                          value={g.name}
                          onChange={(e) => updateArrayItem('guardians', index, 'name', e.target.value)}
                          placeholder="Name"
                          className="caffeine-input"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={g.startDate}
                            onChange={(e) => updateArrayItem('guardians', index, 'startDate', e.target.value)}
                            className="caffeine-input text-sm"
                          />
                          <Input
                            type="date"
                            value={g.endDate}
                            onChange={(e) => updateArrayItem('guardians', index, 'endDate', e.target.value)}
                            className="caffeine-input text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Settlor</Label>
                    <Input
                      value={formData.settlor}
                      onChange={(e) => setFormData({ ...formData, settlor: e.target.value })}
                      placeholder="Settlor Name"
                      className="caffeine-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Beneficiaries</Label>
                    <Select
                      value={formData.beneficiariesType}
                      onValueChange={(value) => setFormData({ ...formData, beneficiariesType: value })}
                    >
                      <SelectTrigger className="caffeine-input">
                        <SelectValue placeholder="Select type..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Named">Named</SelectItem>
                        <SelectItem value="Class">Class</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Beneficiaries / Classes</Label>
                    <Textarea
                      value={formData.beneficiariesClasses}
                      onChange={(e) => setFormData({ ...formData, beneficiariesClasses: e.target.value })}
                      placeholder="List beneficiaries or describe beneficiary classes..."
                      className="caffeine-input min-h-[80px]"
                    />
                  </div>
                </div>
              )}

              {/* Deed Section */}
              <SectionHeader title="Deed & Term" section="deed" icon={Building2} />
              {expandedSections.deed && (
                <div className="space-y-4 pl-4 border-l-2 border-[#E6E0D9]">
                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Trust Deed Date</Label>
                    <Input
                      type="date"
                      value={formData.trustDeedDate}
                      onChange={(e) => setFormData({ ...formData, trustDeedDate: e.target.value })}
                      className="caffeine-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Deed Version / Reference</Label>
                    <Input
                      value={formData.deedVersionReference}
                      onChange={(e) => setFormData({ ...formData, deedVersionReference: e.target.value })}
                      className="caffeine-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Vesting Date</Label>
                    <Input
                      type="date"
                      value={formData.vestingDate}
                      onChange={(e) => setFormData({ ...formData, vestingDate: e.target.value })}
                      className="caffeine-input"
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* ============ SMSF FIELDS ============ */}
          {formData.type === 'SMSF' && (
            <>
              {/* ATO / Identity Section */}
              <SectionHeader title="ATO / Identity" section="identity" icon={Building2} />
              {expandedSections.identity && (
                <div className="space-y-4 pl-4 border-l-2 border-[#E6E0D9]">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">ABN</Label>
                      <Input
                        value={formData.abn}
                        onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                        placeholder="12 345 678 901"
                        className="caffeine-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">TFN</Label>
                      <Input
                        value={formData.tfn}
                        onChange={(e) => setFormData({ ...formData, tfn: e.target.value })}
                        placeholder="123 456 789"
                        className="caffeine-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">ATO Status</Label>
                    <Select
                      value={formData.atoStatus}
                      onValueChange={(value) => setFormData({ ...formData, atoStatus: value })}
                    >
                      <SelectTrigger className="caffeine-input">
                        <SelectValue placeholder="Select status..." />
                      </SelectTrigger>
                      <SelectContent>
                        {ATO_STATUSES.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">ESA (Electronic Service Address)</Label>
                    <Input
                      value={formData.esa}
                      onChange={(e) => setFormData({ ...formData, esa: e.target.value })}
                      className="caffeine-input"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="text-[#2C2420]">GST Registered</Label>
                    <Switch
                      checked={formData.gstRegistered}
                      onCheckedChange={(checked) => setFormData({ ...formData, gstRegistered: checked })}
                    />
                  </div>
                </div>
              )}

              {/* Trustee Structure Section */}
              <SectionHeader title="Trustee Structure" section="parties" icon={Users} />
              {expandedSections.parties && (
                <div className="space-y-4 pl-4 border-l-2 border-[#E6E0D9]">
                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Trustee Type</Label>
                    <Select
                      value={formData.trusteeType}
                      onValueChange={(value) => setFormData({ ...formData, trusteeType: value })}
                    >
                      <SelectTrigger className="caffeine-input">
                        <SelectValue placeholder="Select trustee type..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Individual">Individual</SelectItem>
                        <SelectItem value="Corporate">Corporate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Trustees */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#2C2420]">Trustee(s)</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addArrayItem('trustees', createTrustee)}
                        className="caffeine-btn-ghost"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Trustee
                      </Button>
                    </div>
                    {formData.trustees.map((trustee, index) => (
                      <div key={trustee.id} className="p-3 bg-[#FAF8F5] rounded-lg space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-[#8C8078]">Trustee {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeArrayItem('trustees', index)}
                            className="text-red-500 hover:text-red-700 p-1 h-auto"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        {formData.trusteeType === 'Individual' && (
                          <IndividualSelect
                            value={trustee.individualId}
                            onChange={(value) => {
                              const ind = individuals.find(i => i.id === value);
                              updateArrayItem('trustees', index, 'individualId', value);
                              if (ind) {
                                updateArrayItem('trustees', index, 'name', `${ind.firstName} ${ind.lastName}`);
                              }
                            }}
                          />
                        )}
                        <Input
                          value={trustee.name}
                          onChange={(e) => updateArrayItem('trustees', index, 'name', e.target.value)}
                          placeholder="Name"
                          className="caffeine-input"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={trustee.startDate}
                            onChange={(e) => updateArrayItem('trustees', index, 'startDate', e.target.value)}
                            className="caffeine-input text-sm"
                          />
                          <Input
                            type="date"
                            value={trustee.endDate}
                            onChange={(e) => updateArrayItem('trustees', index, 'endDate', e.target.value)}
                            className="caffeine-input text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {formData.trusteeType === 'Corporate' && (
                    <>
                      <div className="space-y-2">
                        <Label className="text-[#2C2420]">Corporate Trustee ACN</Label>
                        <Input
                          value={formData.corporateTrusteeAcn}
                          onChange={(e) => setFormData({ ...formData, corporateTrusteeAcn: e.target.value })}
                          placeholder="123 456 789"
                          className="caffeine-input"
                        />
                      </div>

                      {/* Directors (for corporate trustee) */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-[#2C2420]">Directors (Corporate Trustee)</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => addArrayItem('directors', createDirector)}
                            className="caffeine-btn-ghost"
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add Director
                          </Button>
                        </div>
                        {formData.directors.map((director, index) => (
                          <div key={director.id} className="p-3 bg-[#FAF8F5] rounded-lg space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-[#8C8078]">Director {index + 1}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeArrayItem('directors', index)}
                                className="text-red-500 hover:text-red-700 p-1 h-auto"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                            <IndividualSelect
                              value={director.individualId}
                              onChange={(value) => {
                                const ind = individuals.find(i => i.id === value);
                                updateArrayItem('directors', index, 'individualId', value);
                                if (ind) {
                                  updateArrayItem('directors', index, 'name', `${ind.firstName} ${ind.lastName}`);
                                }
                              }}
                            />
                            <Input
                              value={director.name}
                              onChange={(e) => updateArrayItem('directors', index, 'name', e.target.value)}
                              placeholder="Full Name"
                              className="caffeine-input"
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                type="date"
                                value={director.startDate}
                                onChange={(e) => updateArrayItem('directors', index, 'startDate', e.target.value)}
                                className="caffeine-input text-sm"
                              />
                              <Input
                                type="date"
                                value={director.endDate}
                                onChange={(e) => updateArrayItem('directors', index, 'endDate', e.target.value)}
                                className="caffeine-input text-sm"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Members Section */}
              <SectionHeader title="Members" section="ownership" icon={Users} />
              {expandedSections.ownership && (
                <div className="space-y-4 pl-4 border-l-2 border-[#E6E0D9]">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#2C2420]">Members</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addArrayItem('members', createMember)}
                        className="caffeine-btn-ghost"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Member
                      </Button>
                    </div>
                    {formData.members.map((member, index) => (
                      <div key={member.id} className="p-3 bg-[#FAF8F5] rounded-lg space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-[#8C8078]">Member {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeArrayItem('members', index)}
                            className="text-red-500 hover:text-red-700 p-1 h-auto"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <IndividualSelect
                          value={member.individualId}
                          onChange={(value) => {
                            const ind = individuals.find(i => i.id === value);
                            updateArrayItem('members', index, 'individualId', value);
                            if (ind) {
                              updateArrayItem('members', index, 'name', `${ind.firstName} ${ind.lastName}`);
                            }
                          }}
                        />
                        <Input
                          value={member.name}
                          onChange={(e) => updateArrayItem('members', index, 'name', e.target.value)}
                          placeholder="Full Name"
                          className="caffeine-input"
                        />
                        <Input
                          value={member.memberNumber}
                          onChange={(e) => updateArrayItem('members', index, 'memberNumber', e.target.value)}
                          placeholder="Member Number (optional)"
                          className="caffeine-input"
                        />
                      </div>
                    ))}
                  </div>

                  {/* Member Balances */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#2C2420]">Member Balances</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addArrayItem('memberBalances', createMemberBalance)}
                        className="caffeine-btn-ghost"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Balance
                      </Button>
                    </div>
                    {formData.memberBalances.map((balance, index) => (
                      <div key={balance.id} className="p-3 bg-[#FAF8F5] rounded-lg space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-[#8C8078]">Balance {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeArrayItem('memberBalances', index)}
                            className="text-red-500 hover:text-red-700 p-1 h-auto"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <Input
                          value={balance.memberName}
                          onChange={(e) => updateArrayItem('memberBalances', index, 'memberName', e.target.value)}
                          placeholder="Member Name"
                          className="caffeine-input"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="number"
                            value={balance.balanceAmount}
                            onChange={(e) => updateArrayItem('memberBalances', index, 'balanceAmount', e.target.value)}
                            placeholder="Balance Amount"
                            className="caffeine-input"
                          />
                          <Input
                            type="date"
                            value={balance.asAtDate}
                            onChange={(e) => updateArrayItem('memberBalances', index, 'asAtDate', e.target.value)}
                            className="caffeine-input"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Governance & Compliance Section */}
              <SectionHeader title="Governance & Compliance" section="compliance" icon={Building2} />
              {expandedSections.compliance && (
                <div className="space-y-4 pl-4 border-l-2 border-[#E6E0D9]">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">SMSF Deed Date</Label>
                      <Input
                        type="date"
                        value={formData.smsfDeedDate}
                        onChange={(e) => setFormData({ ...formData, smsfDeedDate: e.target.value })}
                        className="caffeine-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">Investment Strategy Last Reviewed</Label>
                      <Input
                        type="date"
                        value={formData.investmentStrategyLastReviewed}
                        onChange={(e) => setFormData({ ...formData, investmentStrategyLastReviewed: e.target.value })}
                        className="caffeine-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">Auditor</Label>
                      <Input
                        value={formData.auditor}
                        onChange={(e) => setFormData({ ...formData, auditor: e.target.value })}
                        className="caffeine-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">Administrator</Label>
                      <Input
                        value={formData.administrator}
                        onChange={(e) => setFormData({ ...formData, administrator: e.target.value })}
                        className="caffeine-input"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="text-[#2C2420]">BDBN In Place</Label>
                    <Switch
                      checked={formData.bdbnInPlace}
                      onCheckedChange={(checked) => setFormData({ ...formData, bdbnInPlace: checked })}
                    />
                  </div>

                  {formData.bdbnInPlace && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#2C2420]">BDBN Date Signed</Label>
                        <Input
                          type="date"
                          value={formData.bdbnDateSigned}
                          onChange={(e) => setFormData({ ...formData, bdbnDateSigned: e.target.value })}
                          className="caffeine-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#2C2420]">BDBN Expiry Date</Label>
                        <Input
                          type="date"
                          value={formData.bdbnExpiryDate}
                          onChange={(e) => setFormData({ ...formData, bdbnExpiryDate: e.target.value })}
                          className="caffeine-input"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <Label className="text-[#2C2420]">Pension In Place</Label>
                    <Switch
                      checked={formData.pensionInPlace}
                      onCheckedChange={(checked) => setFormData({ ...formData, pensionInPlace: checked })}
                    />
                  </div>

                  {formData.pensionInPlace && (
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">Pension Commencement Date</Label>
                      <Input
                        type="date"
                        value={formData.pensionCommencementDate}
                        onChange={(e) => setFormData({ ...formData, pensionCommencementDate: e.target.value })}
                        className="caffeine-input"
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ============ FOUNDATION FIELDS ============ */}
          {formData.type === 'Foundation' && (
            <>
              {/* Structure Section */}
              <SectionHeader title="Structure" section="identity" icon={Building2} />
              {expandedSections.identity && (
                <div className="space-y-4 pl-4 border-l-2 border-[#E6E0D9]">
                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Foundation Structure <span className="text-red-500">*</span></Label>
                    <Select
                      value={formData.foundationStructure}
                      onValueChange={(value) => setFormData({ ...formData, foundationStructure: value })}
                    >
                      <SelectTrigger className={`caffeine-input ${errors.foundationStructure ? 'border-red-500' : ''}`} data-testid="foundation-structure-select">
                        <SelectValue placeholder="Select structure..." />
                      </SelectTrigger>
                      <SelectContent>
                        {FOUNDATION_STRUCTURES.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.foundationStructure && <p className="text-sm text-red-500">{errors.foundationStructure}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">ABN</Label>
                      <Input
                        value={formData.abn}
                        onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                        placeholder="12 345 678 901"
                        className="caffeine-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">TFN</Label>
                      <Input
                        value={formData.tfn}
                        onChange={(e) => setFormData({ ...formData, tfn: e.target.value })}
                        className="caffeine-input"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="text-[#2C2420]">GST Registered</Label>
                    <Switch
                      checked={formData.gstRegistered}
                      onCheckedChange={(checked) => setFormData({ ...formData, gstRegistered: checked })}
                    />
                  </div>
                </div>
              )}

              {/* Charity / Endorsement Section */}
              <SectionHeader title="Charity / Endorsement" section="governance" icon={Building2} />
              {expandedSections.governance && (
                <div className="space-y-4 pl-4 border-l-2 border-[#E6E0D9]">
                  <div className="flex items-center justify-between">
                    <Label className="text-[#2C2420]">ACNC Registered</Label>
                    <Switch
                      checked={formData.acncRegistered}
                      onCheckedChange={(checked) => setFormData({ ...formData, acncRegistered: checked })}
                    />
                  </div>

                  {formData.acncRegistered && (
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">ACNC Charity ID</Label>
                      <Input
                        value={formData.acncCharityId}
                        onChange={(e) => setFormData({ ...formData, acncCharityId: e.target.value })}
                        className="caffeine-input"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">Charity Subtype</Label>
                    <Input
                      value={formData.charitySubtype}
                      onChange={(e) => setFormData({ ...formData, charitySubtype: e.target.value })}
                      className="caffeine-input"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="text-[#2C2420]">DGR Endorsed</Label>
                    <Switch
                      checked={formData.dgrEndorsed}
                      onCheckedChange={(checked) => setFormData({ ...formData, dgrEndorsed: checked })}
                    />
                  </div>

                  {formData.dgrEndorsed && (
                    <div className="space-y-2">
                      <Label className="text-[#2C2420]">DGR Item / Category</Label>
                      <Input
                        value={formData.dgrItemCategory}
                        onChange={(e) => setFormData({ ...formData, dgrItemCategory: e.target.value })}
                        className="caffeine-input"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-[#2C2420]">PAF / PuAF</Label>
                    <Select
                      value={formData.pafPuafType}
                      onValueChange={(value) => setFormData({ ...formData, pafPuafType: value })}
                    >
                      <SelectTrigger className="caffeine-input">
                        <SelectValue placeholder="Select type..." />
                      </SelectTrigger>
                      <SelectContent>
                        {PAF_TYPES.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Governance Section */}
              <SectionHeader title="Governance" section="parties" icon={Users} />
              {expandedSections.parties && (
                <div className="space-y-4 pl-4 border-l-2 border-[#E6E0D9]">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#2C2420]">Responsible Persons / Board</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addArrayItem('responsiblePersons', createResponsiblePerson)}
                        className="caffeine-btn-ghost"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Person
                      </Button>
                    </div>
                    {formData.responsiblePersons.map((person, index) => (
                      <div key={person.id} className="p-3 bg-[#FAF8F5] rounded-lg space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-[#8C8078]">Person {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeArrayItem('responsiblePersons', index)}
                            className="text-red-500 hover:text-red-700 p-1 h-auto"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <IndividualSelect
                          value={person.individualId}
                          onChange={(value) => {
                            const ind = individuals.find(i => i.id === value);
                            updateArrayItem('responsiblePersons', index, 'individualId', value);
                            if (ind) {
                              updateArrayItem('responsiblePersons', index, 'name', `${ind.firstName} ${ind.lastName}`);
                            }
                          }}
                        />
                        <Input
                          value={person.name}
                          onChange={(e) => updateArrayItem('responsiblePersons', index, 'name', e.target.value)}
                          placeholder="Full Name"
                          className="caffeine-input"
                        />
                        <Input
                          value={person.role}
                          onChange={(e) => updateArrayItem('responsiblePersons', index, 'role', e.target.value)}
                          placeholder="Role (e.g., Chair, Director)"
                          className="caffeine-input"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={person.startDate}
                            onChange={(e) => updateArrayItem('responsiblePersons', index, 'startDate', e.target.value)}
                            className="caffeine-input text-sm"
                          />
                          <Input
                            type="date"
                            value={person.endDate}
                            onChange={(e) => updateArrayItem('responsiblePersons', index, 'endDate', e.target.value)}
                            className="caffeine-input text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Distributions Section (PAF/PuAF) */}
              {(formData.pafPuafType === 'PAF' || formData.pafPuafType === 'PuAF') && (
                <>
                  <SectionHeader title="Distributions" section="distributions" icon={Building2} />
                  {expandedSections.distributions && (
                    <div className="space-y-4 pl-4 border-l-2 border-[#E6E0D9]">
                      <div className="flex items-center justify-between">
                        <Label className="text-[#2C2420]">Minimum Distribution Tracking Required</Label>
                        <Switch
                          checked={formData.minDistributionRequired}
                          onCheckedChange={(checked) => setFormData({ ...formData, minDistributionRequired: checked })}
                        />
                      </div>

                      {formData.minDistributionRequired && (
                        <>
                          <div className="space-y-2">
                            <Label className="text-[#2C2420]">Distribution % / Requirement</Label>
                            <Input
                              value={formData.distributionPercentage}
                              onChange={(e) => setFormData({ ...formData, distributionPercentage: e.target.value })}
                              placeholder="e.g., 5%"
                              className="caffeine-input"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-[#2C2420]">Distribution Year</Label>
                            <Input
                              value={formData.distributionYear}
                              onChange={(e) => setFormData({ ...formData, distributionYear: e.target.value })}
                              placeholder="e.g., FY2024-25"
                              className="caffeine-input"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {errors.submit && <p className="text-sm text-red-500 text-center">{errors.submit}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="caffeine-btn-ghost" data-testid="cancel-add-entity-btn">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="caffeine-btn" data-testid="save-entity-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : (isEdit ? 'Save Changes' : 'Add Entity')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
